import React, { useContext, useEffect, useRef } from 'react'
import { Section } from './AddNewPatient'
import {
  Alert,
  AlertTitle,
  Box,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import AnDateField from '../../../components/DateTime/DateField'
import moment from 'moment'
import { GlobalContext } from '../../../App'
import { regionLabels } from '../../../helpers/region'
import HospitalNumbers from './HospitalNumbers'
import AddressAutocomplete from '../components/AddressAutocomplete'

const Demographics = ({
  states,
  showDuplicateNHSNoWarning,
  showMissingDataWarning,
  portalOrganisation = null,
}) => {
  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    dob,
    setDOB,
    gender,
    setGender,
    gpSurgery,
    setGpSurgery,
    nhsNumber,
    setNHSNumber,
    hospitalNumbers,
    setHospitalNumbers,
    addressLine1,
    setAddressLine1,
    addressLine2,
    setAddressLine2,
    town,
    setTown,
    city,
    setCity,
    postCode,
    setPostCode,
    location,
    setLocation,
    addressMeta,
    setAddressMeta,
    email,
    setEmail,
    phoneNumber1,
    setPhoneNumber1,
    phoneNumber2,
    setPhoneNumber2,
    nokName,
    setNOKName,
    nokRelationship,
    setNokRelationship,
    nokContactNumber,
    setNokContactNumber,
    setChangesMade,
    existingPatient,
    setAddressUpdated,
  } = states
  const sectionRef = useRef(null)
  const { tenantInfo } = useContext(GlobalContext)
  const labels = regionLabels(tenantInfo?.region)
  const patientAddress = {
    addressLine1,
    addressLine2,
    town,
    city,
    postCode,
    location,
    addressMeta,
  }

  const addressChangeLog = (value = null) => ({
    field: 'demographics.address',
    value:
      (value ||
        [addressLine1, addressLine2, town, city, postCode]
          .filter(Boolean)
          .join(', ')) + '.',
  })

  const clearLocationForManualAddressEdit = (setter) => (value) => {
    setter(value)
    setLocation(undefined)
    setAddressMeta(undefined)
  }

  const selectAddressOption = (option) => {
    setAddressLine1(option.addressLine1 || '')
    setAddressLine2(option.addressLine2 || '')
    setTown(option.town || '')
    setCity(option.city || '')
    setPostCode(option.postCode || '')
    setLocation({
      type: 'Point',
      coordinates: option.coordinates,
    })
    setAddressMeta(option.addressMeta)
    setAddressUpdated(true)

    if (existingPatient) {
      setChangesMade((prev) => ({
        ...prev,
        Address: addressChangeLog(option.label),
      }))
    }
  }

  useEffect(() => {
    if (showDuplicateNHSNoWarning || showMissingDataWarning) {
      const modalBox = document.querySelector('.MuiModal-root .MuiBox-root')
      if (modalBox && sectionRef.current) {
        const sectionTop =
          sectionRef.current.getBoundingClientRect().top -
          modalBox.getBoundingClientRect().top
        modalBox.scrollTo({
          top: sectionTop - 24,
          behavior: 'instant',
        })
      }
    }
  }, [showDuplicateNHSNoWarning, showMissingDataWarning])
  return (
    <div ref={sectionRef}>
      <Section title="Demographics">
        <Grid item xs={12} md={6} lg={4} xl={3}>
          <TextField
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onBlur={() => {
              if (
                existingPatient &&
                lastName !== existingPatient?.demographics?.lastName
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  Surname: { field: 'demographics.lastName', value: lastName },
                }))
                setAddressUpdated(true)
              }
            }}
            fullWidth
            label="Surname"
            autoComplete="none"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4} xl={3}>
          <TextField
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={() => {
              if (
                !!existingPatient &&
                firstName !== existingPatient?.demographics?.firstName
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  'First Name': {
                    field: 'demographics.firstName',
                    value: firstName,
                  },
                }))
                setAddressUpdated(true)
              }
            }}
            fullWidth
            label="First Name"
            autoComplete="none"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4} xl={3}>
          <AnDateField
            date={dob}
            onChange={(dob) => setDOB(dob)}
            onBlur={() => {
              if (
                existingPatient &&
                !moment(dob).isSame(
                  moment(existingPatient?.demographics?.dob),
                  'day',
                )
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  'Date of Birth': {
                    field: 'demographics.dob',
                    value: moment(dob).format('DD/MM/YYYY'),
                  },
                }))
                setAddressUpdated(true)
              }
            }}
            label="Date of Birth"
            fullWidth
            autoComplete="none"
          />
        </Grid>
        <Grid item xs={12} lg={3} xl={2}>
          <TextField
            value={gender}
            select
            fullWidth
            onChange={(e) => setGender(e.target.value)}
            onBlur={() => {
              if (
                existingPatient &&
                gender !== existingPatient?.demographics?.gender
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  Gender: { field: 'demographics.gender', value: gender },
                }))
                setAddressUpdated(true)
              }
            }}
            label="Gender"
            autoComplete="none"
          >
            {['Male', 'Female', 'Non-Binary'].map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} lg={4.5} xl={5}>
          <TextField
            label={labels.nationalIdLabel}
            autoComplete="none"
            value={nhsNumber.replace(/(\d{3})(\d{3})(\d{0,4})/, (_, a, b, c) =>
              c ? `${a} ${b} ${c}` : `${a} ${b}`,
            )}
            onChange={(e) => {
              const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10)
              setNHSNumber(digitsOnly)
            }}
            onBlur={() => {
              if (
                existingPatient &&
                existingPatient?.demographics?.nhsNumber !== nhsNumber
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  'NHS Number': {
                    field: 'demographics.nhsNumber',
                    value: nhsNumber,
                  },
                }))
                setAddressUpdated(true)
              }
            }}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 12,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} lg={4.5} xl={5}>
          <TextField
            label="GP Surgery"
            value={gpSurgery}
            onChange={(e) => setGpSurgery(e.target.value)}
            onBlur={() => {
              if (
                existingPatient &&
                gpSurgery !== existingPatient?.demographics?.gpSurgery
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  'GP Surgery': {
                    field: 'demographics.gpSurgery',
                    value: gpSurgery,
                  },
                }))
                setAddressUpdated(true)
              }
            }}
            fullWidth
            autoComplete="none"
          />
        </Grid>
        <HospitalNumbers
          hospitalNumbers={hospitalNumbers}
          setHospitalNumbers={setHospitalNumbers}
          fixedOrganisation={portalOrganisation}
        />
        {showDuplicateNHSNoWarning && (
          <Grid item xs={12}>
            <Alert severity="error" variant="filled">
              A Patient with this {labels.nationalIdLabel} already exists
            </Alert>
          </Grid>
        )}
        {showMissingDataWarning && (
          <Grid item xs={12}>
            <Alert severity="error" variant="outlined">
              <AlertTitle color="error">Insufficient Data</AlertTitle>
              Please provide the patient's name, date of birth, address, and
              mobility category before submitting — the mobility category is
              used to dispatch the right vehicle.
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="title3">Address</Typography>
        </Grid>
        <Grid item xs={12}>
          <AddressAutocomplete
            address={patientAddress}
            onSelect={selectAddressOption}
            helperText="Choose a result to fill the address and save the patient map point."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Address Line One"
            value={addressLine1}
            onChange={(e) =>
              clearLocationForManualAddressEdit(setAddressLine1)(
                e.target.value,
              )
            }
            onBlur={() => {
              if (
                existingPatient &&
                addressLine1 !== existingPatient?.demographics?.addressLine1
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  Address: addressChangeLog(),
                }))
                setAddressUpdated(true)
              }
            }}
            fullWidth
            autoComplete="none"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Address Line Two"
            value={addressLine2}
            onChange={(e) =>
              clearLocationForManualAddressEdit(setAddressLine2)(
                e.target.value,
              )
            }
            onBlur={() => {
              if (
                existingPatient &&
                addressLine2 !== existingPatient?.demographics?.addressLine2
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  Address: addressChangeLog(),
                }))
                setAddressUpdated(true)
              }
            }}
            fullWidth
            autoComplete="none"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            label="Town"
            value={town}
            onChange={(e) =>
              clearLocationForManualAddressEdit(setTown)(e.target.value)
            }
            onBlur={() => {
              if (
                existingPatient &&
                town !== existingPatient?.demographics?.town
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  Address: addressChangeLog(),
                }))
                setAddressUpdated(true)
              }
            }}
            fullWidth
            autoComplete="none"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            label="City"
            value={city}
            onChange={(e) =>
              clearLocationForManualAddressEdit(setCity)(e.target.value)
            }
            onBlur={() => {
              if (
                existingPatient &&
                city !== existingPatient?.demographics?.city
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  Address: addressChangeLog(),
                }))
                setAddressUpdated(true)
              }
            }}
            fullWidth
            autoComplete="none"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            label="Post Code"
            value={postCode}
            onChange={(e) =>
              clearLocationForManualAddressEdit(setPostCode)(e.target.value)
            }
            onBlur={() => {
              if (
                existingPatient &&
                postCode !== existingPatient?.demographics?.postCode
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  Address: addressChangeLog(),
                }))
                setAddressUpdated(true)
              }
            }}
            fullWidth
            autoComplete="none"
          />
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="title3">Contact Details</Typography>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            label="Patient Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => {
              if (
                existingPatient &&
                email !== existingPatient?.demographics?.email
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  'Patient Email Address': {
                    field: 'demographics.email',
                    value: email,
                  },
                }))
                setAddressUpdated(true)
              }
            }}
            fullWidth
            autoComplete="off"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            label="Patient Phone Number"
            value={phoneNumber1}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 12,
            }}
            onChange={(e) => setPhoneNumber1(e.target.value)}
            onBlur={() => {
              if (
                existingPatient &&
                phoneNumber1 !== existingPatient?.demographics?.phoneNumber1
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  'Patient Phone Number': {
                    field: 'demographics.phoneNumber1',
                    value: phoneNumber1,
                  },
                }))
                setAddressUpdated(true)
              }
            }}
            fullWidth
            autoComplete="none"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            label="Alternative Phone Number"
            value={phoneNumber2}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 12,
            }}
            onChange={(e) => setPhoneNumber2(e.target.value)}
            onBlur={() => {
              if (
                existingPatient &&
                phoneNumber2 !== existingPatient?.demographics?.phoneNumber2
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  'Alternative Phone Number': {
                    field: 'demographics.phoneNumber2',
                    value: phoneNumber2,
                  },
                }))
                setAddressUpdated(true)
              }
            }}
            fullWidth
            autoComplete="none"
          />
        </Grid>
        <Grid item xs={12} />
        <Grid item xs={12} lg={4}>
          <TextField
            label="Next of Kin Name"
            value={nokName}
            onChange={(e) => setNOKName(e.target.value)}
            onBlur={() => {
              if (
                existingPatient &&
                nokName !== existingPatient?.demographics?.nokName
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  'Next of Kin Name': {
                    field: 'demographics.nokName',
                    value: nokName,
                  },
                }))
                setAddressUpdated(true)
              }
            }}
            autoComplete="off"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            label="Next of Kin Relationship"
            value={nokRelationship}
            onChange={(e) => setNokRelationship(e.target.value)}
            onBlur={() => {
              if (
                existingPatient &&
                nokRelationship !==
                  existingPatient?.demographics?.nokRelationship
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  'Next of Kin Relationship': {
                    field: 'demographics.nokRelationship',
                    value: nokRelationship,
                  },
                }))
                setAddressUpdated(true)
              }
            }}
            fullWidth
            autoComplete="none"
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            label="Next of Kin Contact Number"
            value={nokContactNumber}
            onChange={(e) => setNokContactNumber(e.target.value)}
            onBlur={() => {
              if (
                existingPatient &&
                nokContactNumber !==
                  existingPatient?.demographics?.nokContactNumber
              ) {
                setChangesMade((prev) => ({
                  ...prev,
                  'Next of Kin Contact Number': {
                    field: 'demographics.nokContactNumber',
                    value: nokContactNumber,
                  },
                }))
                setAddressUpdated(true)
              }
            }}
            fullWidth
            autoComplete="none"
          />
        </Grid>
      </Section>
    </div>
  )
}

export default Demographics
